import type { CellValueChangedEvent } from 'ag-grid-community';
import { Data, Match } from 'effect';
import { isNil } from 'lodash';
import { useMemo, useRef } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useTheme } from 'styled-components';
import type { Portfolio } from 'venn-api';

import {
  blockAsOfDateSetting,
  blockCustomMetricSettingsState,
  type BlockId,
  blockLimitedRequestSubjects,
  cleanEmptyFromGrid,
  createPCBlockRowKey,
  pcBlock_customField_gridData,
  type StudioRequestSubject,
  subjectToKeyString,
} from 'venn-state';
import {
  AnalysisSubject,
  type AsOfDateSetting,
  assertNotNil,
  isHistoricalSubject,
  pruneNodesWithZeroAllocation,
  useHistoricalPortfolioSnapshot,
} from 'venn-utils';
import { useCustomMetadata } from '../../utils';
import { useAgGridStyle } from '../components/grid/AgGridThemeOverrides';
import {
  type AllocationRowData,
  combinePortfolioRows,
  combineRowsWithCustomMetrics,
  makeColumnDefs,
  makePortfolioRows,
  makeTotalRow,
  MAX_CUSTOM_FIELD_CHARS,
  parsePcField,
  type PCField,
} from './usePortfolioComparisonUtils';
import { useSelectedMetrics } from './useSelectedMetrics';
import { usePageCustomMetricSymbol } from '../../blocks/components/core';

type SubjectType = Data.TaggedEnum<{
  ProForma: { subject: StudioRequestSubject };
  Historical: { subject: StudioRequestSubject; asOfDateSetting: AsOfDateSetting };
}>;

const SubjectType = Data.taggedEnum<SubjectType>();

const createSubject = ({
  subject,
  asOfDateSetting,
}: {
  subject?: StudioRequestSubject;
  asOfDateSetting: AsOfDateSetting;
}): SubjectType | undefined => {
  if (!subject) {
    return undefined;
  }
  if (isHistoricalSubject(subject)) {
    return SubjectType.Historical({
      subject,
      asOfDateSetting,
    });
  }
  return SubjectType.ProForma({ subject });
};

/**
 * Prepares a portfolio for comparison.
 * If portfolio is pro-forma - just returns it as is without modifications
 * If portfolio is historical - fetches snapshot with allocations as of selected date
 */
const usePortfolioForComparison = (subject?: SubjectType): Portfolio | undefined => {
  const snapshotParams: Parameters<typeof useHistoricalPortfolioSnapshot>[0] = Match.value(subject).pipe(
    Match.when(undefined, () => ({ asOfDateSetting: undefined, analysisSubject: undefined })),
    Match.tag('ProForma', ({ subject }) => ({
      asOfDateSetting: undefined,
      analysisSubject: subject.portfolio ? new AnalysisSubject(subject.portfolio, 'portfolio') : undefined,
    })),
    Match.tag('Historical', ({ subject, asOfDateSetting }) => ({
      asOfDateSetting,
      analysisSubject: subject.portfolio ? new AnalysisSubject(subject.portfolio, 'portfolio') : undefined,
    })),
    Match.exhaustive,
  );

  const {
    historicalPortfolioSnapshotResponse: { data: snapshot },
  } = useHistoricalPortfolioSnapshot({
    ...snapshotParams,
    options: {
      suspense: true,
    },
  });

  const prunedSnapshot = snapshot ? pruneNodesWithZeroAllocation(snapshot) : undefined;

  return Match.value(subject).pipe(
    Match.when(undefined, () => undefined),
    Match.tag('ProForma', ({ subject }) => subject.portfolio),
    Match.tag('Historical', () => prunedSnapshot),
    Match.exhaustive,
  );
};

const checkSubjectAndPortfolioNotNil = (
  subjectAndPortfolio: readonly [StudioRequestSubject | undefined, Portfolio | undefined],
): subjectAndPortfolio is [StudioRequestSubject, Portfolio] => {
  const [subject, portfolio] = subjectAndPortfolio;
  return !isNil(subject) && !isNil(portfolio);
};

export const usePortfolioComparison = (blockId: BlockId) => {
  const agGridStyle = useAgGridStyle();
  const theme = useTheme();
  const subjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const blockAsOfDate = useRecoilValue(blockAsOfDateSetting(blockId));
  const selectedMetrics = useSelectedMetrics();
  const customMetricSymbol = usePageCustomMetricSymbol();
  const customMetrics = useRecoilValue(blockCustomMetricSettingsState(blockId));
  const customFieldData = useRecoilValue(pcBlock_customField_gridData(blockId));
  const customFieldsRef = useRef(customFieldData);
  customFieldsRef.current = customFieldData;

  const { data: customMetadataMap = {} } = useCustomMetadata(subjects);

  const subject1 = subjects[0];
  const subject2 = subjects[1];

  const portfolioForComparison1 = usePortfolioForComparison(
    createSubject({ subject: subject1, asOfDateSetting: blockAsOfDate[0] }),
  );
  const portfolioForComparison2 = usePortfolioForComparison(
    createSubject({ subject: subject2, asOfDateSetting: blockAsOfDate[1] }),
  );

  const subjectPortfolioPairs = [
    [subject1, portfolioForComparison1] as const,
    [subject2, portfolioForComparison2] as const,
  ].filter(checkSubjectAndPortfolioNotNil);

  const onCellValueChanged = useRecoilCallback(
    ({ set }) =>
      (event: CellValueChangedEvent<AllocationRowData>) => {
        const { columnGroupKey, columnKey } = parsePcField(assertNotNil(event.colDef.field) as PCField);
        const rowKey = createPCBlockRowKey(assertNotNil(event.data.rowData[columnGroupKey]).rowIdPath);

        set(pcBlock_customField_gridData(blockId), (oldGrid) => {
          const newValue = ((event.newValue as unknown) ?? '').toString().substring(0, MAX_CUSTOM_FIELD_CHARS);
          const newRow = {
            ...oldGrid[rowKey],
            [columnKey]: newValue,
          };
          const newGrid = {
            ...oldGrid,
            [rowKey]: newRow,
          };
          return cleanEmptyFromGrid(newGrid);
        });
      },
    [blockId],
  );

  return useMemo(() => {
    const subjectRowData = subjectPortfolioPairs.map(([subject, portfolio]) =>
      makePortfolioRows(portfolio, subjectToKeyString(subject), customMetadataMap),
    );
    const rowData = combineRowsWithCustomMetrics(
      combinePortfolioRows(subjects, subjectRowData),
      customMetrics,
      customFieldsRef.current,
    );

    const columnDefs = makeColumnDefs(selectedMetrics, subjects, agGridStyle, customMetricSymbol, theme);

    const pinnedBottomRowData = makeTotalRow(subjectPortfolioPairs, customMetrics, customFieldsRef.current, rowData);

    return {
      rowData,
      columnDefs,
      onCellValueChanged,
      pinnedBottomRowData,
    };
  }, [
    subjectPortfolioPairs,
    subjects,
    customMetrics,
    selectedMetrics,
    agGridStyle,
    theme,
    onCellValueChanged,
    customMetadataMap,
    customMetricSymbol,
  ]);
};
